import { Link, graphql } from "gatsby"
import React, { useContext } from "react"
import { IoIosArrowForward } from "react-icons/io"
import { PageContext } from "../../context/PageContext"
import useDatoMicrocopy from "../../hooks/useDatoMicrocopy"
import useRoutePath from "../../hooks/useRoutePath"
import { findRecursive } from "../../utils"
import ExternalLink from "../atoms/ExternalLink"
import MainNav from "../organisms/pageHeader/MainNav"
import { NavLinkNode } from "../organisms/pageHeader/NavLinks"

type RequiredNotNull<T> = {
  [P in keyof T]: NonNullable<T[P]>
}

type Ensure<T, K extends keyof T> = T & Required<RequiredNotNull<Pick<T, K>>>
type Mutable<Type> = { -readonly [Key in keyof Type]: Type[Key] }

type DatoMainNavTreeNode = NonNullable<Queries.DatoMainNavTreeFragment["nodes"]>[number]

type ValidMainNavNode = Ensure<Exclude<DatoMainNavTreeNode, null>, "label"> & { hidden: false }

/**
 * Filter logic of a Menu Item node
 * @param node
 * @returns
 */
function filterMenuItemNode(node: DatoMainNavTreeNode): node is ValidMainNavNode {
  // menu items must have a user visible label
  if (!node?.label) {
    return false // exclude from menu
  }
  // menu items can be marked as "hidden" in DatoCMS. Those should not be included.
  if (node.hidden === true) {
    return false
  }
  // pass the filtering and include in the menu
  return true
}

/**
 * Recursive filtering an sorting of Menu tree nodes.
 * Removes nodes that don't pass `filterMenuItemNode()` and sorts nodes by `position` property.
 *
 * @param nodes -- Menu items
 * @param getRotuePath -- Function to resolve URL path from current menu item data
 * @param routeId -- Current page route record ID used to
 * @returns
 */
function parseTree(
  nodes: readonly DatoMainNavTreeNode[],
  getRoutePath: (routeId: string) => string | undefined,
  routeId?: string
): NavLinkNode[] {
  if (nodes === null) {
    return []
  }
  // console.debug("Parsing MainMenu tree", nodes)

  return nodes
    .reduce<NavLinkNode[]>((res, node) => {
      if (!filterMenuItemNode(node)) {
        return res // nothing to do for this node so return our current progress
      }
      // recurse into next level of menu tree and handle childs first
      const subLinks = parseTree(node?.nodes || [], getRoutePath, routeId)

      // contruct a NavLinkNode with filtered child nodes, add the new object to result
      let link: React.JSX.Element
      if (node.route?.originalId) {
        const path = getRoutePath(node.route.originalId) + (node.urlParams ? node.urlParams : "")
        link = <Link to={path}>{node.label}</Link>
      } else if (node.externalUrl) {
        link = (
          <ExternalLink href={node.externalUrl}>
            {node.label} <IoIosArrowForward style={{ verticalAlign: "middle" }} />
          </ExternalLink>
        )
      } else {
        console.warn("Failed to create MainNav link from Menu Item", node)
        return res
      }

      const navLink: NavLinkNode = {
        id: node.id,
        subLinks,
        link,
        // modifies UI to show this menu has the current your're on under it if true
        hasCurrentPage: !!findRecursive(
          // cast to mutable because `findRecursive()` infers types... somehow
          node.nodes as Mutable<DatoMainNavTreeNode[]>,
          (node: DatoMainNavTreeNode) => node?.route?.originalId === routeId,
          "nodes"
        ),
        // modifies UI to higlight this item if true
        isCurrentPage: node.route?.originalId === routeId,
        position: node.position || 0,
      }
      res.push(navLink)
      return res
    }, [])
    .sort((a, b) => a.position - b.position)
}

const DatoMainNav = (props: Queries.DatoMainNavTreeFragment) => {
  const { locale, originalId } = useContext(PageContext)
  const t = useDatoMicrocopy("common", locale)
  const getRoutePath = useRoutePath()
  if (!props.nodes) {
    return null
  }

  const nodes = parseTree(props.nodes, (routeId: string) => getRoutePath(routeId, locale), originalId)

  return <MainNav menuTree={nodes} labelClose={t("close", "Close")} labelOpen={t("open", "Open")} />
}

export default DatoMainNav

export const query = graphql`
  fragment DatoMenuItem on DatoCmsMenuItem {
    id: originalId
    route {
      originalId
    }
    externalUrl
    urlParams
    label
    position
    hidden
  }
  fragment DatoMainNavTree on DatoCmsMenuItem {
    nodes: treeChildren {
      # first level after root node, the one with products, contacts, sauna etc routes
      ...DatoMenuItem
      nodes: treeChildren {
        ...DatoMenuItem
        nodes: treeChildren {
          ...DatoMenuItem
          nodes: treeChildren {
            ...DatoMenuItem
          }
        }
      }
    }
  }
`
